import React, { useEffect, useRef } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface DateInputProps {
  value?: Date;
  onChange: (date: Date) => void;
  className?: string;
  name?: string;
}

interface DateParts {
  day: number;
  month: number;
  year: number;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className,
  name,
}) => {
  // Use string state for display to allow free editing
  const [displayValues, setDisplayValues] = React.useState(() => {
    const d = value ? new Date(value) : new Date();
    return {
      month: (d.getMonth() + 1).toString().padStart(2, "0"),
      day: d.getDate().toString().padStart(2, "0"),
      year: d.getFullYear().toString(),
    };
  });

  const [focusedField, setFocusedField] = React.useState<
    keyof DateParts | null
  >(null);

  const monthRef = useRef<HTMLInputElement | null>(null);
  const dayRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);
  const isEditingRef = useRef(false);

  useEffect(() => {
    // Only update if not currently editing
    if (!isEditingRef.current) {
      const d = value ? new Date(value) : new Date();
      setDisplayValues({
        month: (d.getMonth() + 1).toString().padStart(2, "0"),
        day: d.getDate().toString().padStart(2, "0"),
        year: d.getFullYear().toString(),
      });
    }
  }, [value]);

  const getDateFromDisplay = (): DateParts => {
    return {
      day: Number(displayValues.day) || 1,
      month: Number(displayValues.month) || 1,
      year: Number(displayValues.year) || new Date().getFullYear(),
    };
  };

  const validateDate = (field: keyof DateParts, value: number): boolean => {
    if (
      (field === "day" && (value < 1 || value > 31)) ||
      (field === "month" && (value < 1 || value > 12)) ||
      (field === "year" && (value < 1000 || value > 9999))
    ) {
      return false;
    }

    // Validate the day of the month
    const currentDate = getDateFromDisplay();
    const newDate = { ...currentDate, [field]: value };
    const d = new Date(newDate.year, newDate.month - 1, newDate.day);
    return (
      d.getFullYear() === newDate.year &&
      d.getMonth() + 1 === newDate.month &&
      d.getDate() === newDate.day
    );
  };

  const handleInputChange =
    (field: keyof DateParts) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      isEditingRef.current = true;

      // Update display value immediately for smooth editing
      setDisplayValues((prev) => ({ ...prev, [field]: inputValue }));

      // If empty, don't validate
      if (inputValue === "") {
        return;
      }

      const newValue = Number(inputValue);

      // Only call onChange when the entry is valid
      if (validateDate(field, newValue)) {
        const currentDate = getDateFromDisplay();
        const newDate = { ...currentDate, [field]: newValue };
        onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
      }
    };

  const initialDateRef = useRef<{ month: string; day: string; year: string }>(
    displayValues,
  );

  const handleBlur =
    (field: keyof DateParts) =>
    (e: React.FocusEvent<HTMLInputElement>): void => {
      setFocusedField(null);
      isEditingRef.current = false;

      if (!e.target.value) {
        setDisplayValues(initialDateRef.current);
        return;
      }

      const newValue = Number(e.target.value);
      const isValid = validateDate(field, newValue);

      if (!isValid) {
        setDisplayValues(initialDateRef.current);
      } else {
        // If the new value is valid, update the initial value and display
        const paddedValue =
          field === "year"
            ? newValue.toString()
            : newValue.toString().padStart(2, "0");

        setDisplayValues((prev) => ({
          ...prev,
          [field]: paddedValue,
        }));

        initialDateRef.current = {
          ...initialDateRef.current,
          [field]: paddedValue,
        };
      }
    };

  const incrementField = (field: keyof DateParts): void => {
    const currentDate = getDateFromDisplay();
    let newDate = { ...currentDate };

    if (field === "day") {
      if (
        currentDate[field] ===
        new Date(currentDate.year, currentDate.month, 0).getDate()
      ) {
        newDate = {
          ...newDate,
          day: 1,
          month: (currentDate.month % 12) + 1,
        };
        if (newDate.month === 1) newDate.year += 1;
      } else {
        newDate.day += 1;
      }
    }

    if (field === "month") {
      if (currentDate[field] === 12) {
        newDate = { ...newDate, month: 1, year: currentDate.year + 1 };
      } else {
        newDate.month += 1;
      }
    }

    if (field === "year") {
      newDate.year += 1;
    }

    const newDisplayValues = {
      month: newDate.month.toString().padStart(2, "0"),
      day: newDate.day.toString().padStart(2, "0"),
      year: newDate.year.toString(),
    };

    setDisplayValues(newDisplayValues);
    initialDateRef.current = newDisplayValues;
    onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
  };

  const decrementField = (field: keyof DateParts): void => {
    const currentDate = getDateFromDisplay();
    let newDate = { ...currentDate };

    if (field === "day") {
      if (currentDate[field] === 1) {
        newDate.month -= 1;
        if (newDate.month === 0) {
          newDate.month = 12;
          newDate.year -= 1;
        }
        newDate.day = new Date(newDate.year, newDate.month, 0).getDate();
      } else {
        newDate.day -= 1;
      }
    }

    if (field === "month") {
      if (currentDate[field] === 1) {
        newDate = { ...newDate, month: 12, year: currentDate.year - 1 };
      } else {
        newDate.month -= 1;
      }
    }

    if (field === "year") {
      newDate.year -= 1;
    }

    const newDisplayValues = {
      month: newDate.month.toString().padStart(2, "0"),
      day: newDate.day.toString().padStart(2, "0"),
      year: newDate.year.toString(),
    };

    setDisplayValues(newDisplayValues);
    initialDateRef.current = newDisplayValues;
    onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
  };

  const handleKeyDown =
    (field: keyof DateParts) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow command (or control) combinations
      if (e.metaKey || e.ctrlKey) {
        return;
      }

      // Prevent non-numeric characters, excluding allowed keys
      if (
        !/^[0-9]$/.test(e.key) &&
        ![
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Delete",
          "Tab",
          "Backspace",
          "Enter",
        ].includes(e.key)
      ) {
        e.preventDefault();
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        incrementField(field);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        decrementField(field);
      }

      if (e.key === "ArrowRight") {
        if (
          e.currentTarget.selectionStart === e.currentTarget.value.length ||
          (e.currentTarget.selectionStart === 0 &&
            e.currentTarget.selectionEnd === e.currentTarget.value.length)
        ) {
          e.preventDefault();
          if (field === "day") monthRef.current?.focus();
          if (field === "month") yearRef.current?.focus();
        }
      } else if (e.key === "ArrowLeft") {
        if (
          e.currentTarget.selectionStart === 0 ||
          (e.currentTarget.selectionStart === 0 &&
            e.currentTarget.selectionEnd === e.currentTarget.value.length)
        ) {
          e.preventDefault();
          if (field === "month") dayRef.current?.focus();
          if (field === "year") monthRef.current?.focus();
        }
      }
    };

  return (
    <div className={cn("relative flex items-center group", className)}>
      {/* Input container */}
      <div className="flex border-1 rounded-lg border-gray-300 dark:border-gray-700 items-center text-sm px-3 py-2 bg-background hover:border-foreground/30 transition-colors focus-within:ring-2 focus-within:ring-ring/30 focus-within:border-ring w-fit">
        <input
          type="text"
          ref={dayRef}
          max={31}
          maxLength={2}
          name=""
          value={displayValues.day}
          onChange={handleInputChange("day")}
          onKeyDown={handleKeyDown("day")}
          onFocus={(e) => {
            setFocusedField("day");
            if (window.innerWidth > 1024) {
              e.target.select();
            }
          }}
          onBlur={handleBlur("day")}
          className="p-0 outline-none w-7 border-none text-center bg-transparent"
          placeholder="DD"
        />
        <span className="opacity-40 px-1">/</span>
        <input
          type="text"
          ref={monthRef}
          max={12}
          maxLength={2}
          value={displayValues.month}
          onChange={handleInputChange("month")}
          onKeyDown={handleKeyDown("month")}
          onFocus={(e) => {
            setFocusedField("month");
            if (window.innerWidth > 1024) {
              e.target.select();
            }
          }}
          onBlur={handleBlur("month")}
          className="p-0 outline-none w-7 border-none text-center bg-transparent"
          placeholder="MM"
        />
        <span className="opacity-40 px-1">/</span>
        <input
          type="text"
          ref={yearRef}
          max={9999}
          maxLength={4}
          value={displayValues.year}
          onChange={handleInputChange("year")}
          onKeyDown={handleKeyDown("year")}
          onFocus={(e) => {
            setFocusedField("year");
            if (window.innerWidth > 1024) {
              e.target.select();
            }
          }}
          onBlur={handleBlur("year")}
          className="p-0 outline-none w-12 border-none text-center bg-transparent"
          placeholder="AAAA"
        />
      </div>
    </div>
  );
};

DateInput.displayName = "DateInput";

export { DateInput };
